import { AfterViewInit, Component, inject, OnDestroy, OnInit, PLATFORM_ID, signal, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Language } from '@meplis/common';
import { TranslateService } from '@ngx-translate/core';
import { CommonModule, Location } from '@angular/common';
import { CommonService } from './shared/services/application/common.service';
import { LeftSidebarComponent } from './shared/components/common/left-sidebar/left-sidebar.component';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { config, dom } from '@fortawesome/fontawesome-svg-core';
import { MEPLIS_ICONS } from '@cnp/icons';
import { APP_BUILD_DATE, APP_COMMIT, APP_VERSION } from './version';
import { HeaderComponent } from './shared/components/common/header/header.component';
import { ProfileModalComponent } from './shared/components/profile/profile-modal/profile-modal.component';
import { AlertService } from '@meplis/services/alert';
import { ModalService } from '@meplis/services/modal';
import { NotificationService } from '@cnp/services/notification';
import { NotificationWebsocketService } from '@meplis/services/notification-websocket';
import { ProfileService } from '@cnp/services';
import { AuthenticationService } from './shared/services/application/authentication.service';
import { EntityInstance } from '@meplis/common';
import { SocialService } from './shared/services/application/social.service';
import { ScrollService } from './shared/services/application/scroll.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { GoogleAnalyticsIdentifiersService } from './shared/services/application/google-analytics-identifiers.service';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { AnalyticsService } from './shared/services/application/analytics.service';
import { UserAccessService } from '@cnp/services';
import { AppVersionCheckService } from './shared/services/application/app-version-check.service';
import { ChatFacade } from '@cnp/services/chat-facade';

@Component({
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    LeftSidebarComponent,
  ],
  standalone: true,
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private _translateService = inject(TranslateService);
  private _router = inject(Router);
  private _location = inject(Location);
  private _commonService = inject(CommonService);
  _iconLibrary = inject(FaIconLibrary);
  private _document = inject<Document>(DOCUMENT);
  private _profileService = inject(ProfileService);
  private _modalService = inject(ModalService);
  private _authService = inject(AuthenticationService);
  private _socialService = inject(SocialService);
  private _notificationService = inject(NotificationService);
  private _notificationWebsocketService = inject(NotificationWebsocketService);
  private _alertService = inject(AlertService);
  private _scrollService = inject(ScrollService);
  private _googleAnalyticsIdentifiersService = inject(GoogleAnalyticsIdentifiersService);
  private _gtmService = inject(GoogleTagManagerService);
  private _analyticsService = inject(AnalyticsService);
  private _userAccessService = inject(UserAccessService);
  private _appVersionCheckService = inject(AppVersionCheckService);
  private _chatFacade = inject(ChatFacade);

  public showHeader: boolean;
  public showSideBar: boolean;
  public showLeftSidebar: boolean = false;
  public showPageContent: boolean = false;
  public userInstance: EntityInstance;

  public blockedHeaderPages = [
    'login',
    'recovery-password',
    'sign-up',
    'threads/',
    'videos/',
  ];

  public blockedSideBarPages = [
    'login',
    'recovery-password',
    'sign-up',
    'threads/',
    'videos/',
    'hubs/',
    'groups/',
    'clinical-cases/',
  ];

  public isMobile = signal(false);

  private userInstanceSubscription: Subscription;
  private _messagesSubscription: Subscription;
  private _e2eeLinkPromptSubscription: Subscription;
  private _isBrowser: boolean;

  constructor() {
    console.log(`Meplis v${APP_VERSION} | ${APP_BUILD_DATE} | ${APP_COMMIT}`);

    this._isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    this._initFontAwesome();
    this._router.events.subscribe(async (event) => {
      if (event instanceof NavigationEnd) {
        const route = this._location.path();
        this._handleRoute(route);

        if (this._isBrowser) {
          this._analyticsService.trackPageView(event.urlAfterRedirects);

          if (!this._scrollService.consumePreserve()) {
            window.scrollTo(0, 0);
          }
        }
      }
    });

    this._authService.onLoggedInOut.subscribe(() => {
      if (this._authService.isLogged) {
        this._verifyIfProfileIsComplete();
        this._checkNotificationPermission();
        this._notificationWebsocketService.connect();
      } else {
        this.userInstance = undefined;
        this._updateSidebarVisibility();
        this._notificationWebsocketService.disconnect();
      }
    });

    this._handleMobileChanges();

    // Evaluate the current route immediately so showHeader/showSideBar are
    // correct on first paint. The router.events subscription above can miss the
    // initial NavigationEnd (it fires during blocking initial navigation, before
    // this component is constructed), which left the header visible on /login.
    this._handleRoute(this._location.path());

    this.userInstanceSubscription = this._socialService
      .onUserInstanceChanged()
      .subscribe((userInstance) => {
        this.userInstance = userInstance;
        this._updateSidebarVisibility();
      });
  }
  ngOnInit(): void {
    if (!this._isBrowser) {
      return;
    }

    // Loading gtm.js here synchronously put it (and the tags it fires, e.g.
    // ContentSquare) on the critical rendering path. GTM's dataLayer queue
    // works whether or not gtm.js has finished loading -- that's how the
    // standard snippet is designed -- so deferring to idle costs no events.
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => this._gtmService.addGtmToDom());
    } else {
      setTimeout(() => this._gtmService.addGtmToDom(), 0);
    }

    this._notificationService.init();
    this._appVersionCheckService.start();
    this._e2eeLinkPromptSubscription = this._chatFacade.e2eeLinkPrompt$.subscribe(
      () => void this._showProtectedConversations()
    );

    if (this._authService.isLogged) {
      this._notificationWebsocketService.connect();
    }

    this._notificationService
      .registerServiceWorker()
      .then((registration) => {
        if (!registration) {
          return;
        }

        this._notificationService
          .requestToken(undefined, registration)
          .then((token) => {
            if (token) {
              console.log('FCM token obtained');
            }
          });
      })
      .catch((error) => {
        console.warn(
          '[AppComponent] Service worker registration failed.',
          error
        );
      });

    this._messagesSubscription = this._notificationService.messages$.subscribe(
      (message) => {
        if (message) {
          console.log('FCM foreground message received:', message);
        }
      }
    );
  }

  ngAfterViewInit(): void {
    this._googleAnalyticsIdentifiersService.init();

    if (this._authService.isLogged) {
      this._verifyIfProfileIsComplete();
    }
  }

  ngOnDestroy(): void {
    this.userInstanceSubscription?.unsubscribe();
    this._messagesSubscription?.unsubscribe();
    this._e2eeLinkPromptSubscription?.unsubscribe();
    this._notificationWebsocketService.disconnect();
  }

  /** D7: this browser has no keys and a protected conversation reached it. Once per tab. */
  private async _showProtectedConversations(): Promise<void> {
    const seenKey = 'mep.e2ee.protectedNoticeSeen';
    try {
      if (sessionStorage.getItem(seenKey)) {
        return;
      }
      sessionStorage.setItem(seenKey, '1');
    } catch {
      // Without storage it is still shown once per load.
    }

    const { ChatProtectedModalComponent } = await import(
      './shared/components/common/chat/chat-protected-modal/chat-protected-modal.component'
    );
    const dialogRef = await this._modalService.openModal(ChatProtectedModalComponent, {}, undefined, 500);
    if ((await firstValueFrom(dialogRef.afterClosed())) !== 'link') {
      return;
    }

    const { ChatLinkBrowserModalComponent } = await import(
      './shared/components/common/chat/chat-link-browser-modal/chat-link-browser-modal.component'
    );
    await this._modalService.openModal(ChatLinkBrowserModalComponent, {}, undefined, 500);
  }

  private async _checkNotificationPermission(): Promise<void> {
    if (Notification.permission === 'granted') {
      return;
    }

    if (Notification.permission === 'denied') {
      await this._alertService.showAlert(
        this._alertService.translateService.instant(
          'NOTIFICATIONS.PERMISSION_BLOCKED_TITLE'
        ),
        this._alertService.translateService.instant(
          'NOTIFICATIONS.PERMISSION_BLOCKED_MESSAGE'
        )
      );
      return;
    }

    // permission === 'default': never asked
    const confirmed = await this._alertService.showConfirmation(
      'NOTIFICATIONS.PERMISSION_ALERT_TITLE',
      'NOTIFICATIONS.PERMISSION_ALERT_MESSAGE',
      'NOTIFICATIONS.PERMISSION_ALERT_CONFIRM',
      'NOTIFICATIONS.PERMISSION_ALERT_CANCEL'
    );

    if (!confirmed) {
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      await this._notificationService.initializePushToken();
    }
  }

  /**
   * Two reasons to open the profile, with different weight: required fields the
   * platform cannot work without, and a missing photo. The modal is told which,
   * so it can say so and -- for the photo alone -- let the user out.
   */
  private _verifyIfProfileIsComplete(): void {
    // Sign-up just created this account and is still uploading the photo it
    // collected; asking for it now would be asking twice.
    if (this._authService.skipProfileCheckOnNextLogin) {
      this._authService.skipProfileCheckOnNextLogin = false;
      return;
    }

    this._profileService.getProfile().subscribe((profile) => {
      const missingRequired = !profile?.roleLevelId;
      const missingAvatar = !profile?.avatar;

      if (!missingRequired && !missingAvatar) {
        return;
      }

      this._modalService.openModal(
        ProfileModalComponent,
        { missingRequired, missingAvatar },
        {
          hasBackdrop: true,
        },
        800
      );
    });
  }

  private _handleMobileChanges(): void {
    const updateTabs = (isMobile: boolean) => {
      this.isMobile.set(isMobile);
    };

    updateTabs(this._commonService.isMobile);

    this._commonService.onIsMobileChange.subscribe(() => {
      updateTabs(this._commonService.isMobile);
    });
  }

  private _setDefaultLanguages(): void {
    const currentLanguages = Object.values(Language);
    this._translateService.addLangs(currentLanguages);
    this._translateService.setFallbackLang('en');

    const browserLang = this._translateService.getBrowserLang();

    if (browserLang) {
      const currentLanguageRegex = new RegExp(currentLanguages.join('|'), 'g');

      this._translateService.use(
        browserLang.match(currentLanguageRegex) ? browserLang : 'en'
      );
    } else {
      this._translateService.use('en');
    }
  }

  private _setLanguage(language: Language): void {
    this._translateService.use(language);
  }

  private _handleRoute(route: string): void {
    this.showHeader = !this._filterBlockedHeaderPages(route);

    //todo
    // if (!this._authService.isLogged) {
    //   this.hideSideBar = true;

    //   return;
    // }

    this.showSideBar =
      !this._filterBlockedSideBarPages(route) && this._authService.isLogged;

    this._updateSidebarVisibility();
  }

  private _filterBlockedSideBarPages(slug: string): boolean {
    return this.blockedSideBarPages.some((link) => slug.indexOf(link) > -1);
  }

  private _filterBlockedHeaderPages(slug: string): boolean {
    return this.blockedHeaderPages.some((link) => slug.indexOf(link) > -1);
  }

  private _updateSidebarVisibility(): void {
    // The public landing page (`/`) is a full-bleed marketing surface: it keeps
    // the header but never shows the left sidebar or the constrained
    // page-content wrapper, regardless of auth state or user type.
    const isHome = this._isHomePage();

    // Restricted (hub-sandboxed) users get the hub layout without the global
    // left sidebar; unrestricted users get the sidebar.
    const isRestricted = this._userAccessService.isRestricted(
      this.userInstance?.type
    );
    this.showLeftSidebar = this.showSideBar && !isRestricted && !isHome;
    this.showPageContent =
      !isHome &&
      !this._filterBlockedHeaderPages(this._location.path()) &&
      (this.showLeftSidebar || isRestricted);
  }

  /** True when the current path is the root landing page. */
  private _isHomePage(): boolean {
    const path = this._location.path().split(/[?#]/)[0].replace(/\/+$/, '');
    return path === '' || path === '/';
  }

  // https://github.com/FortAwesome/angular-fontawesome/issues/407#issuecomment-1934995751
  private _initFontAwesome(): void {
    // This code was heavily inspired by https://www.skovy.dev/blog/server-side-rendering-font-awesome
    // FontAwesome icons loaded the way we do start HUGE initially until the CSS is injected. This injects the CSS immediately.
    const head = this._document.getElementsByTagName('head')[0];
    const styleNode = this._document.createElement('style');

    config.autoAddCss = false; // Disable FA's CSS injection
    styleNode.innerHTML = dom.css(); // grab FA's CSS

    head.appendChild(styleNode);

    // Only the icons meplis-cnp actually renders are registered here (see
    // libs/cnp/src/lib/icons/cnp-icons.ts) -- unlike addIconPacks(fas, far, fab),
    // this keeps the whole Font Awesome library out of the bundle.
    this._iconLibrary.addIcons(...MEPLIS_ICONS);
  }
}
